/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BatchInit, BatchPriority, Configuration, GraphQLQuery, QueryRequest} from '@adobe/exc-app/network';
import fetchApi from './fetch';
import metrics from '../metrics';

interface APIPromise {
  promise: Promise<Response>;
  reject: Function;
  resolve: Function;
}
/**
 * For more info on execHttp query, please use
 * {@link https://git.corp.adobe.com/exc/graphql-service/tree/release-20.03.2/src/graphql-modules/httprequest GraphQLHttp Resolver}
 */
const GRAPHQL_HTTP_QUERY = `
  query GraphQLHttp($request: HttpRequest!){
    execHttp(request: $request){response}
  }
`;

const NA = 'unknown';
const maxRequest = 20;
let logger = metrics.create('exc.module-runtime.network.graphql');
let graphQLConfig = {
  apiGatewayUrl: '',
  appId: ''
};
let batchInterval: Record<BatchPriority, number> = { //in milliseconds
  P1: 0,
  P2: 100,
  P3: 10000
};
let queue: Record<BatchPriority, Array<{req: GraphQLQuery; res: APIPromise}>> = {
  P1: [],
  P2: [],
  P3: []
};
let timerRef: Record<string, number> = {};

const configure = (config: Configuration): void => {
  graphQLConfig = {
    apiGatewayUrl: config.apiGatewayUrl,
    appId: config.appId
  };
};

const request = (input: QueryRequest): Promise<Response> => {
  const preferredRegion = input.preferredRegion;
  const operationName = input.operationName;
  const graphQLQueries = operationName ? Object.assign({operationName}, input.data) : input.data;
  const appId = input.appId || graphQLConfig.appId;
  let url = `${graphQLConfig.apiGatewayUrl}/api/gql/app/${appId}/graphql?appId=${appId}`;
  if (preferredRegion) {
    url = `${graphQLConfig.apiGatewayUrl}/api/gql/app/${appId}/${preferredRegion}/graphql?appId=${appId}&disableRegionForwarding=true`;
  }
  logger.info(`Gql operation: ${operationName}, AppId: ${appId}`);
  return fetchApi.fetch(url, {
    auth: 'Body',
    body: JSON.stringify(graphQLQueries),
    headers: {
      'Content-Type': 'text/plain'
    },
    metadata: input.metadata,
    method: 'POST'
  });
};

function logGqlError(error: string, operation: string = NA, requestId: string = NA): void {
  logger.event('GraphQL.Error', error, {
    appId: graphQLConfig.appId,
    error,
    operation,
    requestId
  });
}

function extractError(res: Response, operation: string = NA): void {
  const requestId: undefined | string = res.headers && res.headers.get('x-request-id') || NA;
  // Clone the response object so that a consumer of the API can read the stream again.
  res.clone().text()
    .then(error => logGqlError(error || 'Unknown error', operation, requestId));
}

function executeBatch(priority: BatchPriority): void {
  const batch = queue[priority];
  //clean up queue for next batch and work on clone.
  queue[priority] = [];
  request({data: batch.map(val => val.req)})
    .then(response => {
      if (response.ok) {
        response.json().then(respArray => {
          batch.forEach((request, index) => {
            if (respArray[index].data) {
              let resp = respArray[index].data.execHttp.response;
              request.res.resolve(new Response(resp.body, resp));
            } else {
              request.res.reject(respArray[index].errors);
            }
          });
        });
      } else {
        //If required, azure FD 503 - timeout errors can fallback to individual api calls.
        extractError(response, 'batch');
        batch.forEach(request => request.res.reject(response));
      }
    }).catch (error => {
      logger.error('Error in GQL Batch call. Falling back to individual api calls', error);
      batch.forEach(val => {
        let req = (val.req.variables || {}).request;
        fetchApi.fetch(req.url, req).then(resp => {
          val.res.resolve(resp);
        }).catch(error => {
          val.res.reject(error);
        });
      });
    });
}

function timer(priority: BatchPriority): void {
  timerRef[priority] = window.setTimeout(() => {
    executeBatch(priority);
  }, batchInterval[priority]);
}

function enqueue(req: GraphQLQuery, priority: BatchPriority): Promise<Response> {
  let res = {} as APIPromise;
  res.promise = new Promise((resolve, reject): void => {
    res.resolve = resolve;
    res.reject = reject;
  });
  queue[priority].push({req, res});
  priority === 'P1' ? (window.clearTimeout(timerRef[priority]), timer(priority))
    : queue[priority].length === 1 && timer(priority);

  //check on max size of queue.
  (Object.keys(queue) as [BatchPriority]).forEach(priority => {
    if (queue[priority].length >= maxRequest) {
      logger.warn(`Received excessive network requests from appId: ${graphQLConfig.appId}. Request count: ${
        queue[priority].length} exceeded batch threshold: ${maxRequest} for ${priority} priority batch`);
      window.clearTimeout(timerRef[priority]);
      executeBatch(priority);
    }
  });
  return res.promise;
}

function batch(input: RequestInfo, init?: BatchInit): Promise<Response> {
  let priority = init && init.priority || 'P2'; //default priority
  //deconstruct requested resource
  let resource = new Request(input, init);
  let headers: Record<string, string> = {};
  resource.headers.forEach((v: string, k: string) => headers[k] = v);
  //apollo takes in JSON serializable body
  return resource.text().then(body => {
    let request = {
      cache: resource.cache,
      credentials: resource.credentials,
      headers,
      method: resource.method,
      mode: resource.mode,
      redirect: resource.redirect,
      url: resource.url
    };
    body.length && Object.assign(request, {body});
    const req: GraphQLQuery = {
      query: GRAPHQL_HTTP_QUERY,
      variables: {
        request
      }
    };
    return enqueue(req, priority);
  });
}

function query(input: QueryRequest): Promise<Response> {
  const operation: string = input.operationName && input.operationName.toString() || NA;
  return request(input)
    .then(res => {
      !res.ok && extractError(res, operation);
      return res;
    })
    .catch(error => {
      logGqlError(error || 'Unknown caught error', operation);
      throw error;
    });
}

export default {
  batch,
  configure,
  query
};
